/** @jsx jsx */
import React from 'react'
import { jsx } from '@emotion/react'

import FormErrorCtx from './FormErrorCtx'
import FormHint from '../../typography/FormHint'
import InputLabel from '../../typography/InputLabel'
import Tooltip from '../../tooltips/Tooltip'
import InputUnavailableCheckbox from './InputUnavailableCheckbox'
import WhiteRoundedInput, { GrayRoundedInput } from './RoundedInput'
import useInputText from '../framework/hooks/useInputText'
import { InputTextProps, InputWithVariant } from '../framework/interfaces'
import theme, { mq } from '../../../theme'

const Input = WhiteRoundedInput.withComponent('input')
const GrayInput = GrayRoundedInput.withComponent('input')

interface Props extends InputTextProps, InputWithVariant {
  labelSmall?: string
  autocomplete?: string
}

const InputText: React.FC<Props> = (props) => {
  const {
    IconLeft,
    className,
    errors,
    groupOrder,
    hasError,
    hint,
    hintComponent,
    isRequired,
    label,
    labelSmall,
    maxLength,
    min,
    max,
    name,
    onBlur,
    placeholder,
    step,
    tooltip,
    type,
    variant,
    autocomplete = '',
  } = props
  const { ctx, elemId, isDisabled, canAutoSubmit, onSubmit } = useInputText(
    props
  )

  let InputComponent = Input
  if (variant === 'gray') {
    InputComponent = GrayInput
  }

  const { formState } = ctx

  return (
    <div
      className={className}
      css={{ marginBottom: theme.margins.inputs.marginBottom }}
    >
      {!!label && (
        <InputLabel
          variant="label2"
          htmlFor={elemId}
          css={[mq('flex-center-v')]}
        >
          {label}
          {!!isRequired && <span>&nbsp;*</span>}
          {!!tooltip && (
            <div css={{ marginLeft: '0.5em' }}>
              <Tooltip tooltip={tooltip} />
            </div>
          )}
        </InputLabel>
      )}
      {!!labelSmall && (
        <InputLabel
          variant="labelSmall"
          htmlFor={elemId}
          css={[mq('flex-center-v')]}
        >
          {labelSmall}
          {!!isRequired && <span>&nbsp;*</span>}
        </InputLabel>
      )}

      <div css={{ position: 'relative' }}>
        <InputComponent
          autoComplete={autocomplete}
          disabled={isDisabled}
          groupOrder={groupOrder}
          id={elemId}
          maxLength={maxLength}
          min={min}
          max={max}
          name={name}
          placeholder={placeholder}
          ref={ctx.register}
          step={step}
          type={type}
          hasErrors={!!errors || !!hasError}
          hasIconLeft={!!IconLeft}
          onFocus={(e) => {
            if (!!props.onFocus) {
              props.onFocus(e)
            }
          }}
          onBlur={(e) => {
            if (!!canAutoSubmit && !!formState.dirtyFields[name]) {
              ctx.setValue('auto_submit', name)
              onSubmit(ctx.getValues())
            }
            if (!!onBlur) {
              onBlur(e)
            }
          }}
          variant={variant}
        />
        {!!IconLeft && (
          <div
            css={{
              position: 'absolute',
              top: '0.7em',
              left: '0.5em',
            }}
          >
            <IconLeft variant="primary" size={16} />
          </div>
        )}
      </div>
      {!!hint && <FormHint>{hint}</FormHint>}
      {!!hintComponent && hintComponent}
      <InputUnavailableCheckbox {...props} />
      <FormErrorCtx disabled={isDisabled} name={name} errors={errors} />
    </div>
  )
}

export default InputText
